/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import SlideModel from './slide-model';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import {
    ILayoutModel, INSERT_PLACEHOLDER_TYPE, LayoutModelSdkTypeV3,
} from '@src/slide/domain/ui-model/element-modeles/Interface/layout-model.interface';
import { transaction } from '@src/slide/domain/slide-app';
import { IPresentationModel } from './Interface/presentation-model.interface';
import { IElementBaseModel } from './Interface/base/element-base-model.interface';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { FOOTER, TITLE, DATE, NUMBER } from '@src/slide/domain/ui-model/constant/place-holder';
import merge from 'lodash/merge';
import ShapeModel from './shape-model';
import { LayoutPropertyConverter } from '@src/slide/domain/ui-model/converter/node-property/page/layout-property-converter';
import { AddPlaceholderType, IPresentationDataModel, IDrawingShapeDataModel, STPlaceholderType, STSlideLayoutType, ILayoutDataModel } from '@tencent/slide-sdk-model';
import { ESpecialOperationType } from '@src/slide/application/service/selection/selected-with-commit-manager.interface';
import { TaskPriority } from '@src/slide/infrastructure/module/dependent/taskManager';
import { observable } from 'mobx';

export default class LayoutModel extends SlideModel implements ILayoutModel {
    public static async convertDataSdkToUi(sdkData, uiData, rawSdkData, option) {
        super.convertDataSdkToUi(sdkData, uiData, rawSdkData, option);
        LayoutPropertyConverter.convertPartialDataFromSdkToUi(sdkData.props, uiData);
    }
    // 是否是SDK提供的预加载数据
    @observable preload = false;
    declare public sdkData: ILayoutDataModel | null;
    public masterId;
    public autoConvertChildren = true;
    public layoutType?: STSlideLayoutType;
    public needConvertChildren = true;   // 是否需要转换子元素
    public readonly isLayout = true;

    public constructor() {
        super();
        this.type = UIModelType.LAYOUT;
    }

    public getRelationMasterModel() {
        return this.presentationRef?.masterList
            .find(masterModel => masterModel?.id === this.masterId);
    }

    public getRelationLayoutModel() {
        return this;
    }

    public async bindSDK(sdkData): Promise<void> {
        super.bindSDK(sdkData);
        if (this.needConvertChildren && this.presentationRef) {
            this.needConvertChildren = this.presentationRef.needConvertChildren;
        }
    }

    public async convertSdkToUiChildrenLogic(rawSdkData, convertRemoveChangeset = false) {
        if (!this.needConvertChildren) return;

        const convertPromises: Promise<void>[] = [];
        convertPromises.push(this.getChildrenConvertPromise(TaskPriority.NORMAL));

        if (this.asyncConvertChildren) {
            await Promise.all(convertPromises);
            this.asyncConvertChildren = false;
        }

        (this.presentationRef as IPresentationModel)
            .layoutChildrenMap[(this.sdkData as ILayoutDataModel)?.id] = this.children
                .filter(child => !child.isPlaceholder);

        // 等待所有子元素convert完成，设置图片异步加载
        this.setSyncLoad();

        if (!convertRemoveChangeset) {
            // 防止mobx调用栈溢出
            // https://github.com/mobxjs/mobx/issues/2214
            this.childrenConverted = true;
        }
    }

    /**
     * 抽屉的版式checkbox选中/取消选中回调
     * @param param0
     */
    public updatePlaceholderFormat({ value: labelName, checked, targetShape }) {
        this.selectionService?.selectedWithCommit.markSpecialOperate(ESpecialOperationType.keep);
        transaction(() => {
            switch (labelName) {
                case TITLE:
                    this.handleTitlePlCheckedChange(checked, targetShape);
                    break;
                case DATE:
                    this.handleDateCheckedChange(checked, targetShape);
                    break;
                case FOOTER:
                    this.handleFooterCheckedChange(checked, targetShape);
                    break;
                case NUMBER:
                    this.handleNumberCheckedChange(checked, targetShape);
                    break;
                default:
                    this.handleOtherCheckedChange(checked, targetShape);
                    break;
            }
        });
        this.selectionService?.selectedElements.clear();
    }

    public handleTitlePlCheckedChange(checked, targetShape?: IElementBaseModel) {
        if (checked) {
            const titlePlSdkData = (this.sdkData as LayoutModelSdkTypeV3)?.api
                .addUniquePlaceholder(AddPlaceholderType.Title);
            this.handleCheckedChange(titlePlSdkData);
        } else {
            const titlePl = targetShape || this.findChildByPlTypeNum(STPlaceholderType.title)
                    || this.findChildByPlTypeNum(STPlaceholderType.ctrTitle);
            this.handleUnCheckedChange(titlePl?.sdkData);
        }
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.MASTER,
            action: TDWActionType.MASTER_TITLE_CHECKBOX_CLICK,
        });
    }

    public handleDateCheckedChange(checked, targetShape?: IElementBaseModel) {
        if (checked) {
            const datePlSdkData = (this.sdkData as LayoutModelSdkTypeV3)?.api
                .addUniquePlaceholder(AddPlaceholderType.Date);
            this.handleCheckedChange(datePlSdkData);
        } else {
            const plTypeNum = STPlaceholderType.dt;
            const datePlSdkData = targetShape?.sdkData || this.findChildByPlTypeNum(plTypeNum)?.sdkData;
            this.handleUnCheckedChange(datePlSdkData);
        }
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.MASTER,
            action: TDWActionType.MASTER_DATE_CHECKBOX_CLICK,
        });
    }

    public handleFooterCheckedChange(checked, targetShape?: IElementBaseModel) {
        if (checked) {
            const footerPlSdkData = (this.sdkData as LayoutModelSdkTypeV3)?.api
                .addUniquePlaceholder(AddPlaceholderType.Footer);
            this.handleCheckedChange(footerPlSdkData);
        } else {
            const plTypeNum = STPlaceholderType.ftr;
            const datePlSdkData = targetShape?.sdkData || this.findChildByPlTypeNum(plTypeNum)?.sdkData;
            this.handleUnCheckedChange(datePlSdkData);
        }
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.MASTER,
            action: TDWActionType.MASTER_FOOTER_CHECKBOX_CLICK,
        });
    }

    public handleNumberCheckedChange(checked, targetShape?: IElementBaseModel) {
        if (checked) {
            const numberPlSdkData = (this.sdkData as LayoutModelSdkTypeV3)?.api
                .addUniquePlaceholder(AddPlaceholderType.SlideNumber);
            this.handleCheckedChange(numberPlSdkData);
        } else {
            const plTypeNum = STPlaceholderType.sldNum;
            const numberPlSdkData = targetShape?.sdkData || this.findChildByPlTypeNum(plTypeNum)?.sdkData;
            this.handleUnCheckedChange(numberPlSdkData);
        }
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.MASTER,
            action: TDWActionType.MASTER_PAGE_NUM_CHECKBOX_CLICK,
        });
    }

    public handleOtherCheckedChange(checked, targetShape?: IElementBaseModel) {
        if (checked) {
            // 暂时不支持添加其他占位符
        } else {
            // 暂时不支持通过children遍历方式查找其他类型的占位符，只能通过传值指定
            this.handleUnCheckedChange(targetShape?.sdkData);
        }
    }

    public addNewPlaceholder(styleProps, actionId: string) {
        const { top, left, width, height } = styleProps.style;
        let plMap = {};
        const sdkData = this.sdkData as LayoutModelSdkTypeV3;
        plMap = {
            [INSERT_PLACEHOLDER_TYPE.INSERT_PLACEHOLDER_HORZ_CONTENT]:
                () => sdkData?.api?.addPlaceholder(AddPlaceholderType.Content),
            [INSERT_PLACEHOLDER_TYPE.INSERT_PLACEHOLDER_VERT_CONTENT]:
                () => sdkData?.api?.addPlaceholder(AddPlaceholderType.Content, true),
            [INSERT_PLACEHOLDER_TYPE.INSERT_PLACEHOLDER_HORZ_TEXT]:
                () => sdkData?.api?.addPlaceholder(AddPlaceholderType.Body),
            [INSERT_PLACEHOLDER_TYPE.INSERT_PLACEHOLDER_VERT_TEXT]:
                () => sdkData?.api?.addPlaceholder(AddPlaceholderType.Body, true),
            [INSERT_PLACEHOLDER_TYPE.INSERT_PLACEHOLDER_PICTURE]:
                () => sdkData?.api?.addPlaceholder(AddPlaceholderType.Picture),
            [INSERT_PLACEHOLDER_TYPE.INSERT_PLACEHOLDER_TABLE]:
                () => sdkData?.api?.addPlaceholder(AddPlaceholderType.Table),
        };
        transaction(() => {
            const titlePlSdkData = plMap[actionId]?.();
            const shape = titlePlSdkData;
            const props = {
                style: {
                    flipH: 0,
                    flipV: 0,
                    height,
                    left,
                    rotate: 0,
                    top,
                    width,
                },
            };
            const uiModel = new ShapeModel();
            if (!uiModel) return;
            uiModel.props = merge(uiModel.props, props);

            const sdkDataArr: Array<any> = [];
            const sdkPropsArr: Array<any> = [];
            const sdkProps = {};
            Object.getPrototypeOf(uiModel).constructor.convertDataUiToSdk(uiModel, sdkProps, this);
            sdkPropsArr.push(sdkProps);
            sdkDataArr.push(shape);
            (this.presentationRef?.sdkData as IPresentationDataModel | null)?.api
                .forEachInTransaction(
                    sdkDataArr as IDrawingShapeDataModel[],
                    (shape: IDrawingShapeDataModel, i) => {
                        shape.api.updateProps(sdkPropsArr[i]);
                    },
                );
            this.handleCheckedChange(titlePlSdkData);
        });

        transaction(() => {
            if (!this.selectionService) return;
            this.selectionService.selectedElements.clear();
            const newSlideModels = this.children.slice(-1);
            this.selectionService.selectedElements.add(...newSlideModels);
        });
    }

    private handleCheckedChange(plSdkData) {
        if (!plSdkData) return;
        (this.sdkData as LayoutModelSdkTypeV3)?.api.linkMatchedShapesOfPlaceholder(plSdkData);
    }

    private handleUnCheckedChange(plSdkData) {
        if (!plSdkData) return;
        this.deleteShapes([plSdkData]);
        (this.sdkData as LayoutModelSdkTypeV3)?.api.unlinkMatchedShapesOfPlaceholder(plSdkData);
    }
}
